/* The licensee console's only door: the hosted server's /v1 routes on this same origin. The account is the console
   session's cookie (HttpOnly, set by POST /v1/session); no account token is ever stored here. A run's own token, for
   the bedside, lives in memory for as long as the page holds it. The server's {error} sentence is the message; a 401
   on an account route sends the reader to sign in. */
import { previewAgents, previewBody, previewCompare, previewOn, previewRuns } from './preview.ts'
import { ApiError } from '../lib/api.ts'
import type { Refusal } from './model/state.ts'
import type { AgentVersion, AgentsBody, VerdictDocumentBody, AnalysisBody, BranchRequest, Comparison, Difference, Membership, ProgramParty, ProgramThresholds, ProgramVerdicts, PublicWorld, Run, RunRequest, StartedRun, Token, Usage, Verify } from './types.ts'

export { ApiError }

/** A refusal of the server's, with the code and the wait it sent when it sent them. */
export class Refused extends ApiError {
  readonly code: string | undefined
  readonly retryAfter: number | undefined
  constructor(status: number, message: string, code?: string, retryAfter?: number) {
    super(status, message)
    this.code = code
    this.retryAfter = retryAfter
  }
}

/** What the state model reads of an error: a refusal's status, code and wait, or only the sentence of anything else. */
export function refusalOf(e: Error): Refusal {
  if (e instanceof Refused) return { message: e.message, status: e.status, code: e.code, retryAfter: e.retryAfter }
  if (e instanceof ApiError) return { message: e.message, status: e.status }
  return { message: e.message }
}

let onSignedOut: () => void = () => undefined
/** Called when the server no longer accepts the console session. */
export function whenSignedOut(fn: () => void): void {
  onSignedOut = fn
}

/** `signingOut`: a 401 here means the session had already ended, which is what signing out asks for, so it is not reported as one that ended. */
type Init = { method?: string; body?: unknown; signal?: AbortSignal; bearer?: string; signingOut?: boolean }

async function call<T>(path: string, init: Init = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      headers: { ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(init.bearer ? { authorization: `Bearer ${init.bearer}` } : {}) },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: init.signal,
    })
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e
    throw new Refused(0, 'The server is not answering. Check your connection and try again.')
  }
  const text = await res.text()
  let body: unknown = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = null
  }
  // An agent token that stops working is the run's business (it ended), not the console session's.
  if (res.status === 401 && !init.bearer && !init.signingOut) onSignedOut()
  if (!res.ok) {
    const b = body as { error?: string; code?: string } | null
    const wait = Number(res.headers.get('retry-after'))
    throw new Refused(res.status, b?.error ?? `${res.status} ${res.statusText}`, typeof b?.code === 'string' ? b.code : undefined, Number.isFinite(wait) && wait > 0 ? wait : undefined)
  }
  return body as T
}

const run = (id: string) => `/v1/runs/${encodeURIComponent(id)}`

const live = {
  world: (signal?: AbortSignal) => call<PublicWorld>('/v1/world', { signal }),

  /** Exchanges the account token for the console cookie. The token goes in this one request and nowhere else. */
  signIn: (token: string) => call<{ signed_in: true }>('/v1/session', { method: 'POST', bearer: token, body: {} }),
  signOut: () => call<unknown>('/v1/session', { method: 'DELETE', signingOut: true }),

  usage: (signal?: AbortSignal) => call<Usage>('/v1/usage', { signal }),

  agents: (signal?: AbortSignal) => call<AgentsBody>('/v1/agents', { signal }),
  register: (body: { name: string; version: string; is?: string }) => call<{ agent: Omit<AgentVersion, 'runs_this_quarter' | 'tested_this_quarter'>; created: boolean }>('/v1/agents', { method: 'POST', body }),
  retire: (name: string, version: string) => call<{ retired: string }>(`/v1/agents/${encodeURIComponent(name)}/${encodeURIComponent(version)}/retire`, { method: 'POST', body: {} }),

  runs: (signal?: AbortSignal) => call<{ runs: Run[] }>('/v1/runs', { signal }),
  startRun: (body: RunRequest) => call<StartedRun>('/v1/runs', { method: 'POST', body }),
  run: (id: string, signal?: AbortSignal) => call<{ run: Run }>(run(id), { signal }),
  analysis: (id: string, signal?: AbortSignal) => call<AnalysisBody>(`${run(id)}/analysis`, { signal }),
  check: (id: string, thresholds: string | null) => call<AnalysisBody>(`${run(id)}/check`, { method: 'POST', body: thresholds === null ? {} : { thresholds } }),
  verify: (id: string) => call<Verify>(`${run(id)}/verify`, { method: 'POST', body: {} }),
  stop: (id: string) => call<{ run: Run }>(`${run(id)}/stop`, { method: 'POST', body: {} }),
  branch: (id: string, body: BranchRequest) => call<{ ok: true; difference: Difference }>(`${run(id)}/branch`, { method: 'POST', body }),
  compare: (a: string, b: string, signal?: AbortSignal) => call<{ comparison: Comparison }>(`/v1/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`, { signal }),

  /** Notes, locally on the server and nowhere else, that the account copied a link to its verdict: a step of the first hour. */
  sharedLink: () => call<{ noted: string }>('/v1/milestones', { method: 'POST', body: { kind: 'first_shared_link' } }),

  membership: (signal?: AbortSignal) => call<Membership>('/v1/membership', { signal }),
  acceptInvitation: (id: string) => call<{ invitation: string; program: { name: string }; status: 'accepted'; accepted: string }>(`/v1/membership/${encodeURIComponent(id)}/accept`, { method: 'POST', body: {} }),
  programParties: (signal?: AbortSignal) => call<{ parties: ProgramParty[] }>('/v1/program/parties', { signal }),
  inviteParty: (account: string) => call<{ party: ProgramParty; note: string }>('/v1/program/parties', { method: 'POST', body: { account } }),
  revokeParty: (id: string) => call<{ party: ProgramParty; note: string }>(`/v1/program/parties/${encodeURIComponent(id)}/revoke`, { method: 'POST', body: {} }),
  programThresholds: (signal?: AbortSignal) => call<{ thresholds: ProgramThresholds | null; note?: string }>('/v1/program/thresholds', { signal }),
  setProgramThresholds: (thresholds: string) => call<{ thresholds: ProgramThresholds }>('/v1/program/thresholds', { method: 'PUT', body: { thresholds } }),
  programVerdicts: (signal?: AbortSignal) => call<ProgramVerdicts>('/v1/program/verdicts', { signal }),
  programVerdict: (run: string, signal?: AbortSignal) => call<VerdictDocumentBody>(`/v1/program/verdicts/${encodeURIComponent(run)}`, { signal }),

  tokens: (signal?: AbortSignal) => call<{ tokens: Token[] }>('/v1/tokens', { signal }),
  revoke: (id: string) => call<{ revoked: string }>(`/v1/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),
}

const REFUSE = () => Promise.reject(new Refused(400, 'This is a preview on made-up records. Leave the preview to do this.'))
/** On a preview the run records are made up and nothing is sent; see preview.ts. */
export const v1: typeof live = previewOn()
  ? { ...live, runs: async () => ({ runs: previewRuns() }), run: async (id: string) => ({ run: previewRuns().find((r) => r.id === id) ?? previewRuns()[0] }), analysis: async (id: string) => previewBody(id), agents: async () => previewAgents(), compare: async (a: string, b: string) => previewCompare(a, b), startRun: REFUSE as typeof live.startRun, stop: REFUSE as typeof live.stop, check: REFUSE as typeof live.check, verify: REFUSE as typeof live.verify, branch: REFUSE as typeof live.branch, register: REFUSE as typeof live.register, retire: REFUSE as typeof live.retire }
  : live

/** The agent's side of a run, driven with the run's own token (the bedside). The same routes an agent's HTTP client calls. */
export const seat = {
  nextCase: (token: string) => call<Record<string, unknown>>('/v1/session/next_case', { method: 'POST', bearer: token, body: {} }),
  call: (token: string, tool: string, args: Record<string, unknown>) => call<Record<string, unknown>>('/v1/session/call', { method: 'POST', bearer: token, body: { tool, args } }),
  endCase: (token: string) => call<Record<string, unknown>>('/v1/session/end_case', { method: 'POST', bearer: token, body: {} }),
}

/** The files to keep: the run analysis as JSON and as the server's page, and the comparison as the server's page. The console draws both documents itself. */
export const pages = {
  analysisHtmlDownload: (id: string) => `${run(id)}/analysis.html?download=1`,
  analysisJsonDownload: (id: string) => `${run(id)}/analysis?download=1`,
  compareHtmlDownload: (a: string, b: string) => `/v1/compare.html?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}&download=1`,
}
